// src/components/desk/ShareMenu.jsx — THE share affordance (owner ruling 2026-07-25,
// CARD_FOOTER_SPEC.md §1 line 3). ONE component, every surface: the card footer's rail and
// the lineage modal's sticky footer render this exact button, so "share" looks and behaves
// the same wherever an agent meets it.
//
// The FACE names the verb (↗ share) and never changes with state — that is what makes it a
// fixed-width rail item. ⚠️ THE TWO LINK KINDS BELOW MOVED TO THE 🔗 PILL on 2026-09-08 (see
// the note above the component); their reasoning is kept because it still governs them there:
//
//   🔗 to client — a tokenized, engagement-tracked share (ShareModal, MEETINGS_MODEL §5).
//                  The price on it is FROZEN: a communication artifact, not a live view.
//   🔗 to agent  — the plain Desk deep link (#datadesk/listing/<id>). Reopens this listing's
//                  lineage modal for a colleague; the server re-checks visibility on open.
//
// ⚠️ These two are NOT interchangeable. Sending a client the agent link exposes the internal
// workbench; sending a colleague the client link hands them a frozen price and burns a share
// token. The menu exists precisely so the choice is made deliberately, once, by name.
//
//   🌐 repost   — reshare this listing to korter / ss / myhome (B244, owner 2026-07-31).
//                  Opens the deal editor's Repost tab; the extension does the posting.
//
// ⚠️ Repost is NOT a link kind — it PUBLISHES, where the two above only hand someone a
// pointer. It lives here because this is the ↗ door agents already reach for, but it is
// separated below the divider and named as an action, so "send someone a link" and "put
// this ad on three public portals" can never be misread as neighbouring flavours of the
// same thing.
//
// Same portal + fixed-position + clamp idiom as ContactPill / MeetingChip / FacetChip: a
// collapsed stack card wraps its front card in a z-indexed, position:relative box, so an
// in-tree popover is trapped in that stacking context and can never paint above the next
// card in the grid, however high its z-index.
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { pill, popoverPos, useCloseOnPageMove } from './primitives.jsx'

const MENU_W = 240

// ── THE TWO PILLS WERE SPLIT BY MEANING (owner 2026-09-08) ──────────────────────────────
// "move links to links pill and add share with others in share pill. share pill will have
// share to platforms and share with others. links will have share link to agent, share link
// to client, and when there are already shared will show history as it does right now."
//
// So the line above — "the two link KINDS live inside the menu" — is no longer true of THIS
// component, and the reasoning is kept rather than deleted because it still governs where
// those two went: they are still two deliberate, separately-named choices, now inside the 🔗
// pill (ShareChip), which is also where the answer to "have I already sent one?" lives. The
// question ↗ used to raise and 🔗 used to answer are now the same door.
//
// What is left here is the pill for ACTS on the thing itself rather than pointers to it:
// publishing it to portals, and handing it to a colleague.
// ⚠️ `onShareOthers` is OMITTED, not disabled, where it cannot work. On a property there is
// no share verb at all (representation is derived from capture and cooperation, never
// granted), so the item simply is not offered there — a menu row that names an action the
// viewer does not have is the lock-with-nothing-behind-it this codebase keeps re-learning.
export function ShareMenu({ onRepost, onShareOthers, className }) {
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const close = () => setPos(null)

  // Scroll/resize: useCloseOnPageMove, so scrolling the panel's own body reads it instead of
  // dismissing it. It reuses this panel's existing ref — one node, one ref.
  useCloseOnPageMove(!!pos, close, panelRef)
  useEffect(() => {
    if (!pos) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      close()
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [pos])

  // Nothing to offer → render nothing, rather than a button that opens an empty menu.
  if (!onRepost && !onShareOthers) return null

  const row = 'w-full text-left flex items-center gap-2 px-1 py-1.5 text-xs text-muted-foreground hover:text-foreground border-b border-border/40 last:border-0'
  return (<>
    <button ref={btnRef} type="button" title={t('share_menu_tooltip')}
      onClick={e => {
        e.stopPropagation()   // card faces are click-to-open; the menu must not also open the modal
        if (pos) return close()
        // Opens UPWARD by preference — this button lives on the card's bottom rail, so
        // downward is where the phone viewport runs out (owner-reported: the menu was
        // cropped mid-row). popoverPos owns that rule; see primitives.jsx.
        setPos(popoverPos(btnRef.current, MENU_W))
      }}
      className={className || pill('neutral', { door: true, extra: 'bg-background text-muted-foreground' })}>
      <Icon name="external-link" />
    </button>
    {pos && createPortal(
      <div ref={panelRef} style={{ ...pos, width: MENU_W }} onClick={e => e.stopPropagation()}
        className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 text-sm overflow-y-auto overscroll-contain">
        {/* Hand it to a colleague: they work it too, and they see what you see. */}
        {onShareOthers && (
          <button type="button" className={row} data-share-others
            onClick={() => { close(); onShareOthers() }}>
            <Icon name="user-plus" /> {t('share_kind_others')}
          </button>
        )}
        {/* A globe rather than a 🔗, because this one does not hand anyone a pointer — it
            publishes the ad. Divided from the row above when both are present: giving a
            colleague access and putting the ad on three public portals must never read as
            neighbouring flavours of the same thing. */}
        {onRepost && (
          <button type="button" className={`${row}${onShareOthers ? ' border-t border-border !mt-1 !pt-2' : ''}`}
            onClick={() => { close(); onRepost() }}>
            <Icon name="globe" /> {t('share_kind_repost')}
          </button>
        )}
      </div>,
      document.body
    )}
  </>)
}
